import Moment from 'moment'
import { extendMoment } from 'moment-range'
import * as React from 'react'
import { Card, H4 } from '@blueprintjs/core'
import { ResponsiveContainer, TooltipProps } from 'recharts'
import { Query, QueryResult } from 'react-apollo'
import gql from 'graphql-tag'
import { SubCardsWrapper, SubCard } from './CounterSubCard'
import Flex from '../UI/Flex'
import LoadingState from './LoadingState'
import NonIdealState from '../UI/NonIdealState'
import { compact } from 'lodash'
import { formatNumber } from './utils'
import Bar from '@ui/charts/Bar'
import css from './dashboard.module.scss'

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
const moment = extendMoment(Moment)

function formatData(data: MoveCount[]) {
  const end = moment.utc().startOf('day')
  const start = end.clone().subtract(30, 'days')

  return Array.from(moment.range(start, end).by('day')).map((d) => {
    const _d = data.find(({ date }) => moment(date).isSame(d))

    return {
      date: d.toDate(),
      count: _d ? _d.count : 0,
    }
  })
}

const query = gql`
  query movesPerDate(
    $cities: [String]
    $startDate: String
    $endDate: String
    $aggregateInterval: MoveOrderAggregate!
  ) {
    viewer {
      company {
        primaryAssignmentPlan {
          moveOrders(
            cities: $cities
            startDate: $startDate
            endDate: $endDate
            ignorePreferredLocations: true
          ) {
            counters {
              movesPerDay
              total
              aggregated(aggregateInterval: $aggregateInterval) {
                date
                count
              }
            }
          }
        }
      }
    }
  }
`

interface IPayload {
  viewer: {
    company: {
      primaryAssignmentPlan: {
        moveOrders: {
          counters: {
            total: number
            movesPerDay: number
            aggregated: MoveCount[]
          }
        }
      }
    }
  }
}

interface IVariables {
  cities: string[]
  startDate: string | undefined | null
  endDate: string | undefined | null
  aggregateInterval: 'day'
}

interface MoveCount {
  count: number
  date: string
}

interface MoveCardProps {
  city: string | null
}

export default function MovesCard({ city }: MoveCardProps) {
  const [startDate] = React.useState(moment().subtract(1, 'month').toDate())
  const [endDate] = React.useState(moment().toDate())

  return (
    <Card style={{ width: '100%' }}>
      <H4>Moves</H4>
      <Flex align="center">
        <p style={{ margin: 0 }}>Last 30 days</p>
      </Flex>
      <div className={css.moves_card_chart_container}>
        <Query
          notifyOnNetworkStatusChange
          query={query}
          variables={{
            cities: compact([city]),
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            aggregateInterval: 'day',
          }}
        >
          {({ loading, data }: QueryResult<IPayload>) => {
            if (loading || !data || !data.viewer) {
              return <LoadingState />
            }

            const { aggregated, movesPerDay, total } =
              data.viewer.company.primaryAssignmentPlan.moveOrders.counters

            const formatted = aggregated.length ? formatData(aggregated) : []
            const height = 226
            return (
              <>
                {formatted.length ? (
                  <ResponsiveContainer width="100%" height={height}>
                    <Bar
                      data={formatted}
                      ydata={{ label: '# OF MOVES', key: 'count' }}
                      xdata={{}}
                      height={height}
                      width={200}
                      layout="horizontal"
                      tooltip={(props) => <CustomTooltip {...props} />}
                      margin={{
                        top: 10,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    />
                  </ResponsiveContainer>
                ) : (
                  <Flex align="center" justify="center" style={{ height: 226 }}>
                    <NonIdealState
                      iconProps={{
                        name: 'box',
                        color: '#ccc',
                        background: 'rgba(0,0,0,0.05)',
                      }}
                      message="To view move history, move at least one person on a floor map or create and finish a move plan."
                    />
                  </Flex>
                )}
                <SubCardsWrapper>
                  <SubCard title="Avg. Employee Moves per day" value={formatNumber(movesPerDay)} />
                  <SubCard title="Total completed moves" value={formatNumber(total)} />
                </SubCardsWrapper>
              </>
            )
          }}
        </Query>
      </div>
    </Card>
  )
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (active && payload) {
    const { count, date } = payload[0].payload
    return (
      <div className={css.moves_card_tooltip_wrapper}>
        <Flex align="center">{moment(date).format('MMM DD')}</Flex>
        <div className={css.count}>
          {formatNumber(count)} move{count === 1 ? '' : 's'}
        </div>
      </div>
    )
  }

  return null
}
